'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Calendar, Clock, User } from 'lucide-react';

interface Blog {
  _id?: string;
  id?: string;
  title: string;
  content?: string;
  description: string;
  status: 'draft' | 'published' | 'scheduled';
  author: string;
  tags: string[];
  coverImageUrl?: string;
  coverImageAlt?: string;
  views: number;
  createdAt?: string;
  updatedAt?: string;
  publishDate?: string;
  slug?: string;
  readTime?: number;
}

interface RecentPostsSidebarProps {
  currentPostId?: string; // Optional: exclude current post
  className?: string;
}

const RecentPostsSidebar: React.FC<RecentPostsSidebarProps> = ({
  currentPostId,
  className = ''
}) => {
  const [recentPosts, setRecentPosts] = useState<Blog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Calculate read time
  const calculateReadTime = (content: string): number => {
    const contentText = content?.replace(/<[^>]*>/g, '') || '';
    const wordCount = contentText.split(/\s+/).filter(word => word.length > 0).length;
    return Math.max(1, Math.ceil(wordCount / 200));
  };

  // Format date
  const formatDate = (dateString: string) => {
    try {
      const dateOnlyMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dateOnlyMatch) {
        const months = [
          'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ];
        const month = months[Number(dateOnlyMatch[2]) - 1];

        return `${month} ${Number(dateOnlyMatch[3])}, ${dateOnlyMatch[1]}`;
      }

      const date = new Date(dateString);

      if (Number.isNaN(date.getTime())) {
        return 'Unknown date';
      }

      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'Unknown date';
    }
  };

  // Fetch recent posts - OPTIMIZED
  useEffect(() => {
    const fetchRecentPosts = async () => {
      try {
        setIsLoading(true);
        setError('');

        const response = await fetch('/api/blogs/recent', {
          cache: 'no-store'
        });
        if (!response.ok) {
          throw new Error('Failed to fetch recent blogs');
        }

        const recentBlogs: Blog[] = await response.json();
        
        // Exclude current post if provided
        const filteredBlogs = currentPostId 
          ? recentBlogs.filter(blog => 
              blog.id !== currentPostId && 
              blog._id?.toString() !== currentPostId
            )
          : recentBlogs;

        setRecentPosts(filteredBlogs.slice(0, 5));
        
      } catch (err) {
        console.error('Error fetching recent posts:', err);
        setError('Failed to load recent posts');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecentPosts();
  }, [currentPostId]);

  if (isLoading) {
    return (
      <div className={className}>
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Recent Posts</h3>
          <div className="space-y-4">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="flex space-x-3">
                  <div className="w-16 h-12 bg-gray-200 rounded-lg"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || recentPosts.length === 0) {
    return null; // Don't show anything if there's an error or no posts
  }

  return (
    <div className={className}>
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 backdrop-blur-sm bg-white/95">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <span className="w-1 h-6 bg-gradient-to-b from-[#AE7D2F] to-[#ECD05B] rounded-full mr-3"></span>
          Recent Posts
        </h3>
        
        <div className="space-y-5">
          {recentPosts.map((post, index) => (
            <Link
              key={post.id || post._id}
              href={`/blog/${post.slug || post.id || post._id}`}
              className="group block"
            >
              <article className="flex space-x-4 p-3 rounded-xl hover:bg-gray-50/80 transition-all duration-300 hover:shadow-md border border-transparent hover:border-gray-200">
                {/* Post Image or Placeholder */}
                <div className="relative w-16 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                  {post.coverImageUrl ? (
                    <Image
                      src={post.coverImageUrl}
                      alt={post.title}
                      fill
                      className="object-cover group-hover:scale-110 transition-transform duration-300"
                      sizes="64px"
                      loading="lazy"
                      quality={75}
                      placeholder="blur"
                      blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#AE7D2F]/20 to-[#ECD05B]/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-[#AE7D2F]/60" />
                    </div>
                  )}
                  
                  {/* Post Number Badge */}
                  <div className="absolute -top-1 -left-1 w-5 h-5 bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {index + 1}
                  </div>
                </div>

                {/* Post Content */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-[#AE7D2F] transition-colors leading-tight mb-2">
                    {post.title}
                  </h4>
                  
                  <div className="flex items-center text-xs text-gray-500 space-x-3">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-3 h-3" />
                      <span>{formatDate(post.publishDate || '')}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="w-3 h-3" />
                      <span>{post.readTime || calculateReadTime(post.content || '')} min read</span>
                    </div>
                  </div>

                  {/* First Tag */}
                  {post.tags && post.tags.length > 0 && (
                    <div className="mt-2">
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-[#AE7D2F]/10 text-[#AE7D2F] rounded-md">
                        {post.tags[0]}
                      </span>
                    </div>
                  )}
                </div>
              </article>
            </Link>
          ))}
        </div>

        {/* View All Posts Link */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <Link
            href="/blog"
            className="block text-center py-3 px-4 bg-gradient-to-r from-[#AE7D2F]/10 to-[#ECD05B]/10 text-[#AE7D2F] rounded-xl font-semibold hover:from-[#AE7D2F]/20 hover:to-[#ECD05B]/20 transition-all duration-300 hover:shadow-md border border-[#AE7D2F]/20"
          >
            View All Posts →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RecentPostsSidebar;
